import { createPartnerCart } from "./partner-cart.js";
/**
 * Subscription tiers
 *
 * Each [data-tier-cta] button adds the configured variant (+ optional
 * selling plan) to the Shopify cart, with line-item properties[Discount]
 * set so the cart visible label matches the existing Skio/Rebuy
 * convention (mirrors subscribe-interval-cards).
 *
 * Prefers window.nativeCartAddItems so the native cart drawer opens;
 * falls back to a FormData POST /cart/add.js when the controller is
 * not loaded.
 */

(() => {
  const partnerState = new WeakMap();
  let partnerStorage;
  try { partnerStorage = window.sessionStorage; } catch (_) {}
  const partnerCart = createPartnerCart({ storage: partnerStorage, root: window.Shopify?.routes?.root || "/" });
  const partnerImpressions = new WeakMap();
  const partnerSession =
    typeof crypto.randomUUID === "function" ? crypto.randomUUID() : null;
  const trackPartner = (button, eventType) => {
    const wrap = partnerWrapFor(button);
    const campaign = wrap && partnerState.get(wrap);
    if (
      !campaign ||
      !partnerSession ||
      !window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()
    )
      return;
    const body = {
      shop: window.Shopify.shop,
      placement: "partner_page",
      offerType: "partner_offer",
      campaignId: campaign.id,
      campaignRevision: campaign.revision,
      offerId: campaign.id,
      offerRevision: campaign.revision,
      eventType,
      eventId: crypto.randomUUID(),
      sessionId: partnerSession,
      market: wrap.dataset.country,
      purchaseMode: "subscription",
      variantId: button.dataset.variantId,
      quantity: 1,
    };
    fetch("https://admin.caffeinecommerce.com/api/events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  };
  const partnerWrapFor = (button) => button?.closest("[data-caffeine-partner]");
  const partnerPagePath = () => {
    const root = window.Shopify?.routes?.root || "/";
    const path = window.location.pathname;
    return (
      root !== "/" && path.startsWith(root)
        ? "/" + path.slice(root.length)
        : path
    ).replace(/\/$/, "");
  };
  const formatPartnerMoney = (cents, wrap) =>
    new Intl.NumberFormat(document.documentElement.lang || "en-US", {
      style: "currency",
      currency: wrap.dataset.currency || "USD",
    }).format(cents / 100);

  const paintPartner = (wrap, campaign, failed = false) => {
    wrap.querySelectorAll("[data-tier-block]").forEach((card) => {
      const button = card.querySelector("[data-tier-cta]");
      const base = Number(card.dataset.basePrice);
      const tier = campaign?.tiers.find(
        (t) =>
          t.variantId ===
            `gid://shopify/ProductVariant/${button.dataset.variantId}` &&
          t.sellingPlanId ===
            `gid://shopify/SellingPlan/${button.dataset.sellingPlanId}`,
      );
      card.querySelector(".subscription-tier__price").textContent =
        formatPartnerMoney(
          tier ? base - Math.floor((base * campaign.percent) / 100) : base,
          wrap,
        );
      const under = card.querySelector(".subscription-tier__under-price");
      if (under)
        under.textContent = tier
          ? `First payment · then ${formatPartnerMoney(base, wrap)} ${under.dataset.originalCopy}`
          : under.dataset.originalCopy;
      button.disabled = button.dataset.ordersPaused === 'true';
    });
    const status = wrap.querySelector("[data-partner-status]");
    if (status)
      status.textContent = campaign
        ? `${campaign.partnerName}: an extra ${campaign.percent}% off your first subscription payment only, on up to ${campaign.maxQuantity} ${campaign.maxQuantity === 1 ? "unit" : "units"} per order. Applied automatically. Other product and order discounts do not combine.`
        : failed
          ? "Partner offer temporarily unavailable. Standard prices are shown."
          : "Standard subscription prices are shown. No additional partner offer is currently available.";
  };

  const loadPartner = async (wrap) => {
    const params = new URLSearchParams({
      shop: window.Shopify?.shop || "",
      campaign: wrap.dataset.caffeinePartner,
      path: partnerPagePath(),
      country: wrap.dataset.country,
    });
    try {
      const response = await fetch(
        `https://admin.caffeinecommerce.com/api/storefront-partner-offers?${params}`,
        { signal: AbortSignal.timeout(8000), cache: "no-store" },
      );
      if (!response.ok) throw new Error("Offer could not be checked");
      const data = await response.json();
      const candidate = data.enabled ? data.campaign : null;
      const campaign =
        candidate &&
        candidate.id === wrap.dataset.caffeinePartner &&
        candidate.pagePath === partnerPagePath() &&
        Number.isFinite(candidate.percent) &&
        candidate.percent > 0 &&
        candidate.percent <= 50 &&
        Array.isArray(candidate.tiers)
          ? candidate
          : null;
      partnerState.set(wrap, campaign);
      paintPartner(wrap, campaign);
      if (!campaign && data.reason === "market") {
        const status = wrap.querySelector("[data-partner-status]");
        if (status) status.textContent = "This partner offer is not available in your selected country. Standard subscription prices are shown.";
      }
      if (
        campaign &&
        partnerImpressions.get(wrap) !== campaign.revision &&
        window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()
      ) {
        partnerImpressions.set(wrap, campaign.revision);
        trackPartner(
          wrap.querySelector("[data-tier-cta][data-selling-plan-id]"),
          "impression",
        );
      }
      return campaign;
    } catch (_) {
      partnerState.set(wrap, null);
      paintPartner(wrap, null, true);
      return null;
    }
  };

  const preparePartner = async (button) => {
    const wrap = partnerWrapFor(button);
    if (!wrap?.dataset.caffeinePartner) return {};
    const previous = partnerState.get(wrap);
    const campaign = await loadPartner(wrap);
    if (previous && (!campaign || campaign.revision !== previous.revision))
      throw new Error(
        "The partner offer changed. Review the updated prices and try again.",
      );
    const eligible = campaign?.tiers.some(
      (t) =>
        t.variantId ===
          `gid://shopify/ProductVariant/${button.dataset.variantId}` &&
        t.sellingPlanId ===
          `gid://shopify/SellingPlan/${button.dataset.sellingPlanId}`,
    );
    if (!eligible) return {};
    return {
      _caffeine_partner_offer: campaign.id,
      _caffeine_partner_revision: String(campaign.revision),
      _caffeine_partner_placement: "partner_page",
      _caffeine_offer_type: "partner_offer",
      _caffeine_offer: campaign.id,
      _caffeine_offer_revision: String(campaign.revision),
      _caffeine_campaign: campaign.id,
      _caffeine_revision: String(campaign.revision),
      _caffeine_placement: "partner_page",
      _caffeine_mode: "subscription",
      _caffeine_market: wrap.dataset.country,
      _caffeine_role: "primary",
    };
  };

  const onClick = async (event) => {
    const btn = event.currentTarget;
    if (!btn || btn.disabled || btn.dataset.ordersPaused === 'true' || btn.getAttribute("aria-busy") === "true") return;

    const variantId = btn.getAttribute("data-variant-id");
    const sellingPlanId = btn.getAttribute("data-selling-plan-id") || "";
    if (!variantId) return;

    const item = {
      id: Number(variantId),
      quantity: 1,
    };
    const properties = {};
    if (sellingPlanId) {
      item.selling_plan = Number(sellingPlanId);
      // Discount property to match the existing Skio/Rebuy cart label.
      // We don't know the actual discount value at click-time without
      // re-walking the allocation, so dataset.discount can be set by liquid
      // if needed; default to leaving it off so Rebuy uses its own label.
      const discountPct = btn.getAttribute("data-discount-pct");
      if (discountPct) {
        properties.Discount = `${discountPct}%`;
      }
    }
    if (Object.keys(properties).length > 0) item.properties = properties;

    const originalLabel = btn.textContent;
    btn.setAttribute("aria-busy", "true");
    btn.textContent = "Adding…";

    try {
      Object.assign(properties, await preparePartner(btn));
      if (properties._caffeine_partner_offer) trackPartner(btn, "add_attempt");
      if (Object.keys(properties).length) item.properties = properties;
      const campaign = partnerState.get(partnerWrapFor(btn));
      if (partnerCart.hasPending() && campaign?.discountMode !== "code")
        throw new Error("An earlier partner cart request needs review. Open your cart before adding another item.");
      if (campaign?.discountMode === "code" && properties._caffeine_partner_offer) {
        await partnerCart.add(item, campaign);
        document.dispatchEvent(new CustomEvent("cart:refresh"));
        const drawer = document.querySelector("cart-drawer");
        if (typeof drawer?.show === "function") drawer.show();
        const status = partnerWrapFor(btn)?.querySelector("[data-partner-status]");
        if (status) status.textContent = "Added to your cart with the Vineyard saving on your first subscription payment.";
      } else if (typeof window.nativeCartAddItems === "function") {
        await window.nativeCartAddItems([item]);
      } else {
        const formData = new FormData();
        formData.append("id", variantId);
        formData.append("quantity", "1");
        if (sellingPlanId) {
          formData.append("selling_plan", sellingPlanId);
        }

        Object.entries(properties).forEach(([key, value]) =>
          formData.append(`properties[${key}]`, value),
        );
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formData,
        });
        if (!response.ok) {
          const txt = await response.text().catch(() => "");
          throw new Error(
            `Cart add failed: ${response.status}${txt ? ` — ${txt}` : ""}`,
          );
        }
        document.dispatchEvent(new CustomEvent("cart:refresh"));
        document.dispatchEvent(
          new CustomEvent("cart:add", { detail: { variantId, sellingPlanId } }),
        );

        // Surface the add in the theme's own cart drawer.
        const drawer = document.querySelector("cart-drawer");
        if (typeof drawer?.show === "function") drawer.show();
      }
      if (properties._caffeine_partner_offer) trackPartner(btn, "conversion");
    } catch (err) {
      if (properties._caffeine_partner_offer) trackPartner(btn, "add_error");
      console.error("[subscription-tiers]", err);
      const status = partnerWrapFor(btn)?.querySelector(
        "[data-partner-status]",
      );
      if (status) {
        status.textContent = err.message || "Could not add the item. Please try again.";
        if (partnerCart.hasPending()) {
          const reviewCart = document.createElement("a");
          reviewCart.href = `${window.Shopify?.routes?.root || "/"}cart`;
          reviewCart.textContent = " Review your cart";
          reviewCart.style.textDecoration = "underline";
          status.appendChild(reviewCart);
        }
      }
      btn.textContent = "Try again";
      setTimeout(() => {
        btn.textContent = originalLabel;
      }, 1800);
      return;
    } finally {
      btn.removeAttribute("aria-busy");
      if (btn.textContent === "Adding…") btn.textContent = originalLabel;
    }
  };

  document.querySelectorAll("[data-tier-cta]").forEach((btn) => {
    btn.addEventListener("click", onClick);
  });

  const storageKeyFor = (code) => `st-partner-discount:${code}`;

  const readStored = (key) => {
    try {
      return sessionStorage.getItem(key);
    } catch (_) {
      return null;
    }
  };

  const writeStored = (key) => {
    try {
      sessionStorage.setItem(key, "1");
    } catch (_) {}
  };

  const applyPartnerDiscount = async (wrap) => {
    if (!wrap) return;
    if (wrap.dataset.caffeinePartner) {
      await loadPartner(wrap);
      return;
    }
    const code = (wrap.getAttribute("data-partner-discount-code") || "").trim();
    const autoApply = wrap.getAttribute("data-auto-apply-discount") === "true";
    if (!code || !autoApply) return;

    const storageKey = storageKeyFor(code);
    if (readStored(storageKey)) return;

    const root = window.Shopify?.routes?.root || "/";
    try {
      let alreadyOn = false;
      try {
        const cartRes = await fetch(`${root}cart.js`, {
          credentials: "same-origin",
        });
        if (cartRes.ok) {
          const cart = await cartRes.json();
          const codes = cart.discount_codes || [];
          alreadyOn = codes.some(
            (entry) =>
              entry &&
              entry.applicable &&
              String(entry.code || "").toUpperCase() === code.toUpperCase(),
          );
        }
      } catch (_) {}

      if (alreadyOn) {
        writeStored(storageKey);
        return;
      }

      await fetch(`${root}discount/${encodeURIComponent(code)}`, {
        credentials: "same-origin",
      });
      writeStored(storageKey);
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    } catch (_) {}
  };

  // Also handle dynamically-rendered tiers (e.g. after theme editor saves).
  document.addEventListener("shopify:section:load", (e) => {
    if (!e.target) return;
    e.target.querySelectorAll("[data-tier-cta]").forEach((btn) => {
      btn.addEventListener("click", onClick);
    });
    e.target.querySelectorAll(".subscription-tiers-wrap").forEach((wrap) => {
      applyPartnerDiscount(wrap);
    });
  });

  document.querySelectorAll(".subscription-tiers-wrap").forEach((wrap) => {
    applyPartnerDiscount(wrap);
  });
})();
